import * as React from 'react';

import './styles/price_calculator';
import './styles/people_count_selector';
import { IResort, IPeople, IPrice, ICurrency } from '../ShoppingCart/types';
import t from './locales';
import { validateSnowItemNoDateFunc } from '../ShoppingCart/validations';
import SnowItemCartButtonNoDate from '../ShoppingCart/SnowItemCartButtonNoDate';
import SnowItemPurchaseButtonNoDate from '../ShoppingCart/SnowItemPurchaseButtonNoDate';
import { priceWithCommas, isOneAdultItem, isOneChildItem, isOneSeniorItem, isOneStudentItem } from '../utils/commonHelper';
import { getLocale } from '../utils/pathHelper';
import { ICoupon, IPeopleCount } from './types';
import CouponInterface from './CouponInterface';
import PeopleCountSelector from './PeopleCountSelector';

interface IProps {
  itemId: number;
  itemName: string;
  price: IPrice;
  dateDuration: number;
  maxParticipants?: number;
  limitedTo?: 'adult' | 'child' | 'student' | 'senior';
  noCart?: boolean;
  resort?: IResort;
  localCurrency?: ICurrency;
  deposit: number;
  isCharge: boolean;
  isSeasonPass: boolean;
  forAxess: boolean;
  isPartnerEc?: boolean;
  coupons: ICoupon[];
  selectedPeopleCount: IPeopleCount;
  merchantCustomizedStudentCategoryNameFlag: boolean;
  merchantCustomizedSeniorCategoryNameFlag: boolean;
}

interface IState {
  people: IPeople;
  numberOfDays: number;
  selectedCoupon: ICoupon | null;
}

class SnowItemPriceCalculatorNoDate extends React.Component<IProps, IState> {
  static defaultProps = {
    noCart: false,
  };

  constructor(props: IProps) {
    super(props);
    if (this.props.isCharge) {
      this.state = {
        people: this.props.selectedPeopleCount || {
          adultNum: this.props.limitedTo == 'adult' ? 1 : 0,
          childNum: this.props.limitedTo == 'child' ? 1 : 0,
          studentNum: this.props.limitedTo == 'student' ? 1 : 0,
          seniorNum: this.props.limitedTo == 'senior' ? 1 : 0,
        },
        numberOfDays: 1,
        selectedCoupon: null
      };
    } else {
      this.state = {
        people: this.props.selectedPeopleCount || {
          adultNum: isOneAdultItem() ? 1 : 0,
          childNum: isOneChildItem() ? 1 : 0,
          studentNum: isOneStudentItem() ? 1 : 0,
          seniorNum: isOneSeniorItem() ? 1 : 0,
        },
        numberOfDays: this.props.dateDuration,
        selectedCoupon: null
      };
    }

    this.handleCouponSelect = this.handleCouponSelect.bind(this);
  }

  numberOfVouchers() {
    //todo: 一時対応ので、シーズン終わったらちゃんとUIに対応します
    let vouchersPerItem = 1;
    if (this.props.itemId == 1804 || this.props.itemId == 2115) {
      vouchersPerItem = 2;
    }
    return this.state.numberOfDays / this.props.dateDuration * vouchersPerItem;
  }

  getLocaleByEnv() {
    // 本番環境の場合、localeをsubdomainから取得
    let locale = getLocale({ underscore: true });
    // localとstg環境で言語切り替えボタンを押したら、localeをユーザー選択した言語から取得
    if (process.env.RAILS_ENV === 'development' || process.env.RAILS_ENV === 'staging') {
      let buttonElement = document.querySelector('#lang-button .btn');
      locale = buttonElement.textContent.trim();
    }
    return locale;
  }

  handleNumberOfPeopleChange(name: string, increment: number) {
    this.setState((prevState) => {
      const updatedPeople = {
        ...prevState.people,
        [name]: Math.max(0, prevState.people[name] + increment),
      };
      const totalParticipants = updatedPeople.adultNum + updatedPeople.childNum + updatedPeople.studentNum + updatedPeople.seniorNum;

      if (this.props.maxParticipants && totalParticipants > this.props.maxParticipants) {
        alert(t.pleaseSelectValidNumberOfParticipants({ max: this.props.maxParticipants }));
        return prevState;
      }

      return {
        ...prevState,
        people: updatedPeople,
      };
    });
  }

  handleNumberOfDaysChange(event: any): void {
    this.setState({ numberOfDays: event.currentTarget.selectedIndex });
  }

  renderActionButton() {
    const { itemId, itemName, price, resort, maxParticipants, deposit, isCharge, isSeasonPass, forAxess } = this.props;
    const { people } = this.state;
    const validate = validateSnowItemNoDateFunc({ itemId, people, numberOfVouchers: this.numberOfVouchers() });
    if (this.props.noCart) {
      return (
        <SnowItemPurchaseButtonNoDate
          itemId={itemId}
          people={people}
          validate={validate}
        />
      );
    }
    return (
      <SnowItemCartButtonNoDate
        itemId={itemId}
        itemName={itemName}
        price={price}
        people={people}
        numberOfVouchers={this.numberOfVouchers()}
        resort={resort}
        maxParticipants={maxParticipants}
        validate={validate}
        deposit={deposit}
        charge={isCharge}
        seasonpass={isSeasonPass}
        axess={forAxess}
      />
    );
  }

  renderPeopleCountRadioButton() {
    const locale = this.getLocaleByEnv();

    const { price, localCurrency, merchantCustomizedStudentCategoryNameFlag, merchantCustomizedSeniorCategoryNameFlag } = this.props;
    const { rate } = localCurrency;
    const selectedPeopleCount = this.state.people;

    const localizePrice = (price) => {
      return price * rate;
    };

    const findClickedRadioButton = (obj) => {
      for (const [key, value] of Object.entries(obj)) {
        if (value !== 0) {
          return key.replace('Num', '');;
        }
      }
      return 'adult';
    };

    const clickedRadioButton = findClickedRadioButton(selectedPeopleCount);

    let studentLabel = merchantCustomizedStudentCategoryNameFlag ? 'middleSchoolStudentNum' : 'studentNum';
    let seniorLabel = merchantCustomizedSeniorCategoryNameFlag ? 'ageOverSixtyNum' : 'seniorNum';

    const priceMapping = {
      adult: { label: 'adultNum', priceKey: 'adultPrice', personType: 'adult' },
      child: { label: 'childNum', priceKey: 'childPrice', personType: 'child' },
      student: { label: studentLabel, priceKey: 'studentPrice', personType: 'student' },
      senior: { label: seniorLabel, priceKey: 'seniorPrice', personType: 'senior' }
    };

    const priceArray = Object.keys(priceMapping).map(key => {
      const { label, priceKey, personType } = priceMapping[key];
      return {
        value: personType,
        label: label,
        price: price[priceKey],
        localizePrice: localizePrice(price[priceKey])
      }
    });

    return (
      <div>
        <PeopleCountSelector
          options={priceArray}
          locale={locale}
          localCurrency={localCurrency}
          defaultCheck={clickedRadioButton}
        />
      </div>

    )
  }

  renderPeopleCountSelector() {
    const locale = this.getLocaleByEnv();
    const { limitedTo, price, localCurrency, isPartnerEc, merchantCustomizedStudentCategoryNameFlag, merchantCustomizedSeniorCategoryNameFlag } = this.props;
    const { groupPrice, groupFullPrice } = price;
    const { rate } = localCurrency;

    const peopleTypes = [
      { name: 'adult' },
      { name: 'child' },
      { name: 'student' },
      { name: 'senior' }
    ];

    const localizePrice = (price) => {
      return price * rate;
    };

    const totalPeople = () => {
      return peopleTypes.reduce((acc, type) => acc + this.state.people[`${type.name}Num`], 0);
    };

    return (
      <div>
        {groupPrice && (
          <div className="people-count-selector mb-3">
            <span className="font-weight-bold">{t.groupPrice()}</span>
            <div className="people-count-selector__price-info__price">
              {groupFullPrice && (
                <span className="people-count-selector__price-info__price--full">
                  {locale === 'ja'
                    ? `${priceWithCommas(groupFullPrice)}円`
                    : `${localCurrency.code} ${priceWithCommas(localizePrice(groupFullPrice))}`}
                </span>
              )}
              <span className="people-count-selector__price-info__price--discounted">
                {locale === 'ja'
                  ? `${priceWithCommas(groupPrice)}円`
                  : `${localCurrency.code} ${priceWithCommas(localizePrice(groupPrice))}`}
              </span>
            </div>
          </div>
        )}
        {peopleTypes.map((type) => {
          const priceName = `${type.name}Price` as 'adultPrice' | 'childPrice' | 'studentPrice' | 'seniorPrice';
          const numName = `${type.name}Num` as 'adultNum' | 'childNum' | 'studentNum' | 'seniorNum';
          let discountedPrice = price[priceName];
          let fullPrice = price[type.name + 'FullPrice'];

          if ((discountedPrice == null && groupPrice == null) || (limitedTo !== null && limitedTo !== type.name)) {
            return null;
          }

          if (discountedPrice === fullPrice) {
            fullPrice = null;
          }

          let localizedDiscountedPrice;
          let localizedFullPrice;

          if (locale === 'ja') {
            localizedDiscountedPrice = `${priceWithCommas(discountedPrice)}円`;
            if (fullPrice) {
              localizedFullPrice = `${priceWithCommas(fullPrice)}円`;
            }
          } else {
            localizedDiscountedPrice = `${localCurrency.code} ${priceWithCommas(localizePrice(discountedPrice))}`;
            if (fullPrice) {
              localizedFullPrice = `${localCurrency.code} ${priceWithCommas(localizePrice(fullPrice))}`;
            }
          }

          return (
            <div key={numName} className="people-count-selector mb-3">
              <div className="d-flex justify-content-between align-items-center">
                {numName == 'adultNum' && <span className="people-count-selector__price-info__num-name">{t[numName]()}</span>}
                {numName == 'childNum' && <span className="people-count-selector__price-info__num-name">{t[numName]()}</span>}
                {numName == 'studentNum' && merchantCustomizedStudentCategoryNameFlag && <span className="people-count-selector__price-info__num-name">{t['middleSchoolStudentNum']()}</span>}
                {numName == 'studentNum' && !merchantCustomizedStudentCategoryNameFlag && <span className="people-count-selector__price-info__num-name">{t[numName]()}</span>}
                {numName == 'seniorNum' && merchantCustomizedSeniorCategoryNameFlag && <span className="people-count-selector__price-info__num-name">{t['ageOverSixtyNum']()}</span>}
                {numName == 'seniorNum' && !merchantCustomizedSeniorCategoryNameFlag && <span className="people-count-selector__price-info__num-name">{t[numName]()}</span>}
                {!groupPrice && (
                  <div className="people-count-selector__price-info__price">
                    {fullPrice && (
                      <span className="people-count-selector__price-info__price--full">{localizedFullPrice}</span>
                    )}
                    <span className="people-count-selector__price-info__price--discounted">{localizedDiscountedPrice}</span>
                  </div>
                )}
              </div>
              <div className="people-count-selector__counter">
                <button
                  type="button"
                  className={`people-count-selector__counter_button people-count-selector__counter_button--decrement ${this.state.people[numName] === 0 ? 'muted' : ''}`}
                  onClick={() => this.handleNumberOfPeopleChange(numName, -1)}
                >
                  <i className="fa fa-minus fa-sm"></i>
                </button>
                <span className={`people-count-selector__counter__value ${this.state.people[numName] > 0 ? 'font-weight-bold' : ''}`}>{this.state.people[numName]}</span>
                <button
                  type="button"
                  className="people-count-selector__counter_button people-count-selector__counter_button--increment"
                  onClick={() => this.handleNumberOfPeopleChange(numName, 1)}
                  disabled={totalPeople() >= 10}
                >
                  <i className="fa fa-plus fa-sm"></i>
                </button>
              </div>
              {isPartnerEc && (
                <input type="hidden" name={`order_group[orders_attributes][0][${type.name}_num]`} value={this.state.people[numName]} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderDepositAmount() {
    const { deposit, localCurrency } = this.props;
    const { rate } = localCurrency;
    const locale = this.getLocaleByEnv();
    let localizedDeposit: any;

    if (locale.toString() == 'ja') {
      localizedDeposit = deposit.toString() + '円';
    } else {
      localizedDeposit = priceWithCommas(deposit * rate).toString() + localCurrency.code;
    }

    return (
      <div className="mt-2">
        <div className="people-count-selector__price-info">
          <span className="mr-1" >{t['icCardDeposit']()}</span>
          <span className="people-count-selector__price-info__price people-count-selector__price-info__price--deposit">{locale === 'ja' ? `${deposit}円` : `JPY ${deposit}`}</span>
          { locale != 'ja' && (
            <span className="text-sm text-muted ml-1">({localizedDeposit})</span>
          )}
        </div>
        <div className="text-sm">
          {t['icCardDepositNote']()}
        </div>
      </div>
    )
  }

  handleCouponSelect(selectedCoupon: ICoupon) {
    this.setState({ selectedCoupon });
  }

  ticketPrice() {
    let ticketPrice: number = 0;
    const { price } = this.props;
    const { groupPrice } = price;

    if (groupPrice) {
      return groupPrice;
    }

    const selectedPeopleCount = this.state.people;
    const peopleTypes = [
      { name: 'adult'},
      { name: 'child'},
      { name: 'student'},
      { name: 'senior'}
    ];

    peopleTypes.map((type) => {
      const priceName = `${type.name}Price` as 'adultPrice'|'childPrice'|'studentPrice'|'seniorPrice';

      ticketPrice += price[priceName] * selectedPeopleCount[`${type.name}Num`]
    });

    return ticketPrice;
  }

  getTotalHeadcount() {
    const { people } = this.state;

    return Object.values(people).reduce((total, num) => total + num, 0);
  }

  render() {
    const { coupons, isPartnerEc, localCurrency, deposit } = this.props;
    const { selectedCoupon } = this.state;
    const ticketPrice = this.ticketPrice();
    let discount = 0;

    const totalHeadcount = this.getTotalHeadcount();
    const totalDeposit = deposit * totalHeadcount;

    if (selectedCoupon) {
      if (selectedCoupon.discountRate != null) {
        discount = ticketPrice * (selectedCoupon.discountRate);
      } else if (selectedCoupon.discountAmount != null) {
        discount = selectedCoupon.discountAmount;
      }
    }

    const finalPrice = (ticketPrice + totalDeposit) - discount;
    const locale = this.getLocaleByEnv();

    let localizedFinalPrice: number | string = finalPrice;
    let localizedTicketPrice: number | string = ticketPrice;
    let localCurrencyCode = 'JPY';

    if (locale !== 'ja' && localCurrency) {
      const { rate, code } = localCurrency;
      localizedFinalPrice = finalPrice * rate;
      localizedTicketPrice = ticketPrice * rate;
      localCurrencyCode = code;

      if (localCurrencyCode === 'USD') {
        localizedFinalPrice = localizedFinalPrice.toFixed(2);
        localizedTicketPrice = localizedTicketPrice.toFixed(2);
      } else {
        localizedFinalPrice = Math.round(localizedFinalPrice);
        localizedTicketPrice = Math.round(localizedTicketPrice);
      }
    }

    return (
      <div>
        {(!this.props.isCharge || this.props.limitedTo == null) && (
          <div className={`price-calculator__section ${this.props.isPartnerEc ? "card bg-snow-white my-4 p-3 rounded-0" : ""}`}>
            {this.props.isPartnerEc && (
              <p className="font-weight-bold">{t.selectNumOfPeopleAndDate()}</p>
            )}
            {this.props.deposit > 0 && this.renderDepositAmount()}
            {(!this.props.isSeasonPass) && this.renderPeopleCountSelector()}
            {this.props.isSeasonPass && this.renderPeopleCountRadioButton()}
          </div>
        )}
        {(this.props.isCharge && this.props.limitedTo !== null) && (
          <div className={`price-calculator__section ${this.props.isPartnerEc ? "card bg-snow-white my-4 p-3 rounded-0" : ""}`}>
            {this.props.isPartnerEc && (
              <p className="font-weight-bold">{t.selectNumOfPeopleAndDate()}</p>
            )}
            {this.props.deposit > 0 && this.renderDepositAmount()}
            {this.renderPeopleCountSelector()}
          </div>
        )}
        {this.props.isPartnerEc && (
          <div className="price-calculator__coupon card bg-snow-white my-4 p-3 rounded-0">
            <CouponInterface
              coupons={coupons}
              isPartnerEc={isPartnerEc}
              onSelectCoupon={this.handleCouponSelect}
            />
          </div>
        )}
        {this.props.isPartnerEc && (deposit > 0 && totalHeadcount > 0) && (
          <div className="col-lg-5 text-center mx-auto">
            <div className="d-flex align-items-center justify-content-between">
              <p>{`${t.icCardDeposit()} × ${totalHeadcount}`}</p>
              <p className="text-primary">{locale === 'ja' ? `${totalDeposit} 円` : `JPY ${totalDeposit}`}</p>
            </div>
          </div>
        )}
        {this.props.isPartnerEc && selectedCoupon && (
          <div className="col-lg-5 text-center mx-auto">
            <div className="d-flex align-items-center justify-content-between">
              <p>{t.couponDiscount()}</p>
              <p className="text-primary">{locale === 'ja' ? `-${discount} 円` : `-JPY ${discount}`}</p>
            </div>
          </div>
        )}
        {this.props.isPartnerEc && !this.props.isSeasonPass && (
          <div className="col-lg-5 text-center mx-auto">
            <div className="d-flex justify-content-between">
              <p className="font-weight-bold">{t.paymentTotal()}</p>
              <div className="d-flex align-items-center justify-content-between text-right">
                {locale !== 'ja' && <p className="text-muted text-sm mr-4">JPY {finalPrice}</p>}
                <p className="font-weight-bold text-primary">{locale === 'ja' ? `${finalPrice} 円` : `${localCurrencyCode} ${localizedFinalPrice}`}</p>
                <input type="hidden" id="payment_amount" value={`${finalPrice}`} />
              </div>
            </div>
          </div>
        )}
        {!this.props.isPartnerEc && (
          <div className="price-calculator__cta">
            {this.renderActionButton()}
          </div>
        )}
      </div>
    );
  }
}

export default SnowItemPriceCalculatorNoDate;
